import { useEffect, useCallback, useMemo } from 'react';
import { useLanguageContext } from '../../../../../contexts/LanguageContext';
import Dialog from '../../../../../components/Dialog';
import { ActionButton } from '../../../../../components/Button';
import { Grid } from '@mui/material';
import SelectField from '../../../../../components/SelectField';
import DefaultConstants from '../../../../../data/Constants';
import { ServicePriorityData, ServicePriorityEnum, ServiceStatusData, ServiceStatusEnum } from '../../../../../data/Enums';
import { IDefaultDialogFilterProps } from '../../../../../interfaces/IReport';
import { useAuthContext } from '../../../../../contexts/AuthContext';
import IEnterprise from '../../../../../interfaces/IEnterprise';
import useState from '../../../../../hooks/useState';
import IUser from '../../../../../interfaces/IUser';
import IDepartment from '../../../../../interfaces/IDepartment';
import { IRoom } from '../../../../../interfaces/IPartnerPms';
import Operators from '../../../../../data/Operators';
import DateField from '../../../../../components/DateField';


export interface IServiceOrdersReportFilters {
    enterprise: IEnterprise | undefined;
    status: ServiceStatusEnum[];
    priority: ServicePriorityEnum[];
    room: IRoom[];
    department: IDepartment[];
    responsible: IUser[];
    startDateOperator: string;
    startDate: Date | undefined;
    estimatedCompletionDateOperator: string;
    estimatedCompletionDate: Date | undefined;
}

const ServiceOrdersFilters: React.FunctionComponent<IDefaultDialogFilterProps> = ({
    open, 
    handleClose,
    handleOpenPreview,
    enterpriseList
}) => {
    const { translate } = useLanguageContext();
    const { enterprise } = useAuthContext();

    const state = useState<IServiceOrdersReportFilters>({
        enterprise: enterprise,
        status: [],
        priority: [],
        room: [],
        department: [],
        responsible: [],
        startDateOperator: Operators[0],
        startDate: undefined,
        estimatedCompletionDateOperator: Operators[0],
        estimatedCompletionDate: undefined,
    });


    useEffect(() => {
        if (open) {
            state.reset();
        }
        // eslint-disable-next-line
    }, [open]);


    useEffect(() => {
        state.update('enterprise', state.values.enterprise);
        // eslint-disable-next-line
    }, [state.values.enterprise]);


    useEffect(() => {
        state.update('responsible', []);
        // eslint-disable-next-line
    }, [state.values.department]);


    const rooms = useMemo(() => {
        return state.values.enterprise?.partnerPms.rooms ?? [];
    }, [state.values.enterprise]);

    const departments = useMemo(() => {
        return state.values.enterprise?.departments ?? [];
    }, [state.values.enterprise]);

    const responsibles = useMemo(() => {
        return state.values.department.reduce((acc, curr) => {
            return acc.concat(curr.users);
        }, [] as IUser[]);
    }, [state.values.department]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='close' onClick={handleClose} />,
            <ActionButton action='view' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        handleOpenPreview(state.values);
        // eslint-disable-next-line
    }, [state.values]);


    return (
        <Dialog 
            open={open}
            handleClose={handleClose}
            title={translate('serviceOrders')}
            fullWidth={true}
            dialogActions={dialogActions}
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween'
            maxWidth='md'
            fullHeight={true}
        >
            <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                    <SelectField
                        label={translate('enterprise')}
                        name='enterprise'
                        value={state.values.enterprise?.id ?? 0}
                        onChange={e => {
                            const { name, value } = e.target;
                            const enterprise = enterpriseList.find(e => +e.id === +value);
                            state.update(name, enterprise);
                        }}
                        required={true}
                        data={enterpriseList.map(e => {
                            return {
                                value: +e.id,
                                text: e.name
                            };
                        })}
                    />
                </Grid>

                <Grid item xs={12} />

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('serviceStatus')}
                        name='status'
                        value={state.values.status.length > 0 ? state.values.status : [-1]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const status = state.values.status.length > 0 && values.includes(-1) ? [] : Object.values(ServiceStatusData).filter(s => values.includes(s.key)).map(s => s.key);
                            state.update(name, status);
                        }}
                        required={false}
                        multiple={true}
                        data={[{ value: -1, text: translate('all') }].concat(Object.values(ServiceStatusData).map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('priority')}
                        name='priority'
                        value={state.values.priority.length > 0 ? state.values.priority : [-1]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const priority = state.values.priority.length > 0 && values.includes(-1) ? [] : Object.values(ServicePriorityData).filter(s => values.includes(s.key)).map(s => s.key);
                            state.update(name, priority);
                        }}
                        required={false}
                        multiple={true}
                        data={[{ value: -1, text: translate('all') }].concat(Object.values(ServicePriorityData).map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('room')}
                        name='room'
                        value={state.values.room.length > 0 ? state.values.room.map(r => +r.id) : [-1]}
                        required={false}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const room = state.values.room.length > 0 && values.includes(-1) ? [] : rooms.filter(r => values.includes(+r.id));
                            state.update(name, room);
                        }}
                        data={[{ value: -1, text: translate('all') }].concat(rooms.map(r => {
                            return {
                                value: +r.id,
                                text: r.description
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('department')}
                        name='department'
                        value={state.values.department.length > 0 ? state.values.department.map(d => +d.id) : [-1]}
                        required={false}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const department = state.values.department.length > 0 && values.includes(-1) ? [] : departments.filter(d => values.includes(+d.id));
                            state.update(name, department);
                        }}
                        data={[{ value: -1, text: translate('all') }].concat(departments.map(d => {
                            return {
                                value: +d.id,
                                text: d.name
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('responsible')}
                        name='responsible'
                        value={state.values.responsible.length > 0 ? state.values.responsible.map(r => +r.id) : [-1]}
                        required={false}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const responsible = state.values.responsible.length > 0 && values.includes(-1) ? [] : responsibles.filter(r => values.includes(+r.id));
                            state.update(name, responsible);
                        }}
                        data={[{ value: -1, text: translate('all') }].concat(responsibles.map(r => {
                            return {
                                value: +r.id,
                                text: r.fullName
                            };
                        }))}
                    />
                </Grid>

                <Grid item xs={12} />

                <Grid item md={2} sm={2} xs={4}>
                    <SelectField
                        label={translate('operator')}
                        name='startDateOperator'
                        value={state.values.startDateOperator}
                        required={true}
                        onChange={e => state.update(e.target.name, e.target.value)}
                        data={Operators}
                    />
                </Grid>

                <Grid item md={4} sm={4} xs={8}>
                    <DateField
                        label={translate('startDate')}
                        format='DD/MM/YYYY'
                        name='startDate'
                        value={state.values.startDate}
                        onChange={value => state.update('startDate', value)}
                        required={false}
                    />
                </Grid>

                <Grid item md={6} xs={12} />

                <Grid item md={2} sm={2} xs={4}>
                    <SelectField
                        label={translate('operator')}
                        name='estimatedCompletionDateOperator'
                        value={state.values.estimatedCompletionDateOperator}
                        required={true}
                        onChange={e => state.update(e.target.name, e.target.value)}
                        data={Operators}
                    />
                </Grid>

                <Grid item md={4} sm={4} xs={8}>
                    <DateField
                        label={translate('estimatedCompletionDate')}
                        format='DD/MM/YYYY'
                        name='estimatedCompletionDate'
                        value={state.values.estimatedCompletionDate}
                        onChange={value => state.update('estimatedCompletionDate', value)}
                        required={false}
                    />
                </Grid>
            </Grid>
        </Dialog>
    );
}

export default ServiceOrdersFilters;